import { Component, Injector, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedModalFormComponent } from '@app/components/deprecated-modal-form/deprecated-modal-form.component';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { RoutingStateService } from '@app/modules/common/services/routing-state.service';
import { TemplateType } from '@app/modules/performance/enums/template-type.enum';
import { PerformanceMenuService, ReviewService, SurveyModuleMenuService } from '@app/modules/performance/services';
import { TemplateIndexTable } from '@app/modules/performance/services/tables/template-table/template-index.table';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { MenuService } from '@app/services/menu/menu.service';
import { IndexView } from '@common/views/index/index.view';
import { Template } from '@models/performance/template.model';
import { Subject, Subscription } from 'rxjs';

@Component({
    templateUrl: './template-index.template.html',
    styleUrls: ['./template-index.style.scss'],
})
export class TemplateIndexView extends IndexView {
    @ViewChild('templateModal', { static: true }) templateModal!: DeprecatedModalFormComponent;

    menuService: MenuService;
    template = new Template();
    table: TemplateIndexTable;
    createPermission;
    canCreate: Subject<boolean> = new Subject<boolean>();
    $canCreateSubscription: Subscription;
    performanceRoute = '/performance';
    surveyRoute = '/surveys';
    hasPerformanceModuleSplit = false;
    isDeleteDialogOpen = false;
    selectedTemplateByAction: Template | null = null;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private routingState: RoutingStateService,
        private analyticService: AnalyticService,
        private featureService: FeatureService,
        private notify: NotifyService,
        private readonly auth: AuthService,
        private reviewService: ReviewService,
        injector: Injector
    ) {
        super();

        // Unfortunately, we share this view across two different modules.
        // This would be fine if we didn't nest our menus within the view (some modules have a parent routing component)
        // As a workaround we can base our menu service superclass off of the current URL
        try {
            if (this.router.url.includes('survey')) {
                this.menuService = injector.get(SurveyModuleMenuService);
            } else {
                this.menuService = injector.get(PerformanceMenuService);
            }
        } catch (e) {
            return;
        }
    }

    async ngOnInit(): Promise<void> {
        this.hasPerformanceModuleSplit = await this.featureService.has(FeatureFlag.performanceModuleSplit);

        if (this.router.url.startsWith(this.performanceRoute)) {
            this.template.type = TemplateType.REVIEW;
            this.createPermission = Template.performancePermission.edit;
            this.table = new TemplateIndexTable(
                this.auth.company.id,
                TemplateType.REVIEW,
                this.hasPerformanceModuleSplit
            );
            this.table.showMenuColumnOptions = await this.featureService.has(FeatureFlag.performanceReviewImprovements);
        }

        if (this.router.url.startsWith(this.surveyRoute)) {
            this.createPermission = Template.surveyPermission.edit;
            // Want the old permission to be applied if feature flag is off so that they can access creating a template
            if (!this.hasPerformanceModuleSplit) {
                this.createPermission = Template.performancePermission.edit;
            }

            this.template.type = TemplateType.SURVEY;

            this.table = new TemplateIndexTable(
                this.auth.company.id,
                TemplateType.SURVEY,
                this.hasPerformanceModuleSplit
            );
        }

        if (this.routingState.previousUrl === '/surveys/center/create' && this.auth.can(this.createPermission)) {
            this.templateModal.show();
        }

        this.canCreate.next(this.auth.can(this.createPermission));

        this.table.reload();
    }

    ngAfterViewInit(): void {
        this.$canCreateSubscription = this.canCreate.subscribe((canCreate) => {
            if (this.routingState.previousUrl === '/surveys/center/create' && canCreate) {
                this.templateModal.show();
            }
        });
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        this.selectedTemplateByAction = data as Template;
        switch (menuItem.event) {
            case TemplateIndexTable.duplicateOption.event:
                this.cloneAndRedirectToTemplate();
                break;
            case TemplateIndexTable.deleteOption.event:
                this.isDeleteDialogOpen = true;
                break;
        }
    }

    cloneAndRedirectToTemplate(): void {
        if (!this.selectedTemplateByAction) {
            return;
        }

        this.isLoading = true;

        this.reviewService.cloneAndRedirectToTemplate(this.selectedTemplateByAction, () => {
            this.isLoading = false;
        });
    }

    closeDeleteDialog(): void {
        this.isDeleteDialogOpen = false;
    }

    async onDelete(): Promise<void> {
        this.closeDeleteDialog();

        this.isLoading = true;

        try {
            await this.selectedTemplateByAction.delete();

            await this.table.reload();
        } catch (err) {
            this.notify.error(err.message || 'performance.templateIndex.deleteDialog.defaultError');
        } finally {
            this.isLoading = false;
        }
    }

    onCreate(): void {
        this.template
            .save()
            .then((res) => {
                // Track this event only if this component is loaded from the Performance module.
                if (this.router.url.startsWith(this.performanceRoute)) {
                    this.analyticService.trackEvent(AnalyticEvents.PerformanceCreateTemplateSave);
                }

                // Track this event only if this component is loaded from the Surveys module.
                if (this.router.url.startsWith(this.surveyRoute)) {
                    this.analyticService.trackEvent(AnalyticEvents.SurveysCreateTemplateSave);
                }

                this.router.navigate([res.id], { relativeTo: this.route });
            })
            .catch((err) => {
                this.notify.error(err.message || 'surveys.templates.index.couldNotCreateTemplate');
            });
    }

    ngOnDestroy(): void {
        this.$canCreateSubscription.unsubscribe();
    }
}
